/*
 * Esprit Salopette — casquette offerte
 * ------------------------------------------------------------------------------------------------
 * Garde dans le panier exactement une casquette « cadeau » par salopette éligible.
 *
 * - La casquette cadeau est une vraie ligne de panier (produit casquette, variante = couleur),
 *   repérée par la propriété privée `_es_gift: "1"`. Les casquettes achetées seules ne sont jamais touchées.
 * - La gratuité vient de la remise automatique Shopify « Achetez X, obtenez Y ». Si la ligne cadeau
 *   n'est pas réellement à 0 € dans le panier, elle est retirée (réglage « Retirer la casquette si la
 *   remise ne la rend pas gratuite ») : on ne facture jamais une casquette annoncée comme offerte.
 * - Toutes les opérations passent par une file unique : pas de doublon en cas de clics répétés.
 * - Déclencheurs : chargement de page, retour arrière (bfcache), événement Prestige `cart:change`
 *   (ajout, quantité, suppression), et vérification juste avant le passage au paiement.
 */
(() => {
  const configElement = document.getElementById('es-gift-config');
  if (!configElement || window.EsGift) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  if (!config.enabled || !config.variants || config.variants.length === 0) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const PREFERRED_KEY = 'es_gift_variant';
  const BLOCKED_KEY = 'es_gift_discount_missing';
  const STATUS_KEY = 'es_gift_status';
  const RELOAD_KEY = 'es_gift_reloads';
  const BLOCK_DURATION = 10 * 60 * 1000;

  const safeStorage = (type) => ({
    get(key) {
      try { return window[type].getItem(key); } catch (error) { return null; }
    },
    set(key, value) {
      try { window[type].setItem(key, value); } catch (error) { /* stockage indisponible */ }
    },
    remove(key) {
      try { window[type].removeItem(key); } catch (error) { /* stockage indisponible */ }
    }
  });

  const local = safeStorage('localStorage');
  const session = safeStorage('sessionStorage');

  /* ------------------------------------------------------------------------------------------ */
  /* Accès panier                                                                                */
  /* ------------------------------------------------------------------------------------------ */

  async function api(path, body) {
    const response = await fetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: body
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });

    const json = await response.json().catch(() => ({}));

    if (!response.ok) {
      const error = new Error(json.description || json.message || `Erreur panier ${response.status}`);
      error.status = response.status;
      throw error;
    }

    return json;
  }

  const getCart = () => api('cart.js');
  const sum = (lines) => lines.reduce((total, line) => total + line.quantity, 0);
  const isGiftProduct = (item) => item.product_id === config.productId;
  const isGift = (item) => isGiftProduct(item) && item.properties && item.properties._es_gift === '1';
  const isEligible = (item) => config.eligible.indexOf(item.product_id) !== -1 && !isGiftProduct(item);
  const variantById = (id) => config.variants.find((variant) => variant.id === Number(id));

  /* Nombre d'unités réellement rendues gratuites par une remise sur la ligne. */
  function freeUnits(line) {
    if (!line.quantity || !line.original_line_price) return 0;
    const unitPrice = line.original_line_price / line.quantity;
    if (unitPrice <= 0) return line.quantity;
    const discounted = line.original_line_price - line.final_line_price;
    return Math.floor((discounted + 0.5) / unitPrice);
  }

  /* ------------------------------------------------------------------------------------------ */
  /* Messages                                                                                    */
  /* ------------------------------------------------------------------------------------------ */

  let currentStatus = session.get(STATUS_KEY);
  session.remove(STATUS_KEY);

  function renderStatus() {
    const message = currentStatus ? config.strings[currentStatus] : '';

    document.querySelectorAll('[data-es-gift-status]').forEach((node) => {
      node.textContent = message || '';
      node.hidden = !message;
    });
  }

  function setStatus(key) {
    currentStatus = key;
    renderStatus();
    document.dispatchEvent(new CustomEvent('es-gift:status', { detail: { key, message: key ? config.strings[key] : '' } }));
  }

  /* ------------------------------------------------------------------------------------------ */
  /* Choix de la couleur                                                                         */
  /* ------------------------------------------------------------------------------------------ */

  function getPreferred() {
    const variant = variantById(local.get(PREFERRED_KEY));
    return variant && variant.available ? variant.id : null;
  }

  function setPreferred(variantId) {
    if (variantById(variantId)) local.set(PREFERRED_KEY, String(variantId));
  }

  /* Répartit `need` casquettes entre les couleurs disponibles, en commençant par la couleur choisie. */
  function planVariants(cart, need) {
    const preferred = getPreferred();
    const ordered = config.variants.slice().sort((a, b) => (b.id === preferred) - (a.id === preferred));
    const plan = [];
    let left = need;

    for (const variant of ordered) {
      if (!left) break;
      if (!variant.available) continue;

      const alreadyInCart = sum(cart.items.filter((item) => item.variant_id === variant.id));
      const room = variant.limit === null ? left : Math.max(0, variant.limit - alreadyInCart);
      const quantity = Math.min(room, left);

      if (quantity > 0) {
        plan.push({ id: variant.id, quantity });
        left -= quantity;
      }
    }

    return { plan, left };
  }

  /* ------------------------------------------------------------------------------------------ */
  /* Synchronisation                                                                             */
  /* ------------------------------------------------------------------------------------------ */

  function discountBlocked() {
    const since = Number(session.get(BLOCKED_KEY));
    return since && Date.now() - since < BLOCK_DURATION;
  }

  /* Une passe : renvoie true si le panier a été modifié. */
  async function syncOnce() {
    const cart = await getCart();
    const giftLines = cart.items.filter(isGift);
    const giftQuantity = sum(giftLines);
    const eligibleQuantity = sum(cart.items.filter(isEligible));
    const optedOut = cart.attributes && cart.attributes._es_gift_optout === '1';

    let target = optedOut ? 0 : eligibleQuantity;

    if (config.requireDiscount && giftQuantity > 0) {
      const freeGift = giftLines.reduce((total, line) => total + Math.min(freeUnits(line), line.quantity), 0);
      const otherCapDiscounted = cart.items.some((item) => isGiftProduct(item) && !isGift(item) && freeUnits(item) > 0);

      if (freeGift === 0 && !otherCapDiscounted) {
        /* Aucune remise appliquée : la casquette serait facturée. On la retire. */
        session.set(BLOCKED_KEY, String(Date.now()));
        console.warn('[ES cadeau] La casquette cadeau n’est pas remisée à 0 € : vérifier la remise automatique « Achetez X, obtenez Y ».');
        setStatus('discountMissing');
        target = 0;
      } else if (freeGift < giftQuantity) {
        target = Math.min(target, freeGift);
      }
    }

    if (discountBlocked()) {
      target = Math.min(target, giftQuantity);
      if (giftQuantity === 0 && eligibleQuantity > 0 && !optedOut) setStatus('discountMissing');
    }

    if (giftQuantity === target) {
      if (currentStatus === 'soldOut' && target === eligibleQuantity) setStatus(null);
      return false;
    }

    if (giftQuantity > target) {
      let excess = giftQuantity - target;
      const updates = {};

      for (const line of giftLines.slice().reverse()) {
        if (!excess) break;
        const removed = Math.min(line.quantity, excess);
        updates[line.key] = line.quantity - removed;
        excess -= removed;
      }

      await api('cart/update.js', { updates });
      return true;
    }

    const { plan, left } = planVariants(cart, target - giftQuantity);
    let changed = false;

    for (const step of plan) {
      const existing = giftLines.find((line) => line.variant_id === step.id);

      try {
        if (existing) {
          await api('cart/change.js', { id: existing.key, quantity: existing.quantity + step.quantity });
        } else {
          await api('cart/add.js', { items: [{ id: step.id, quantity: step.quantity, properties: { _es_gift: '1' } }] });
        }
        changed = true;
      } catch (error) {
        if (error.status === 422) {
          /* Stock épuisé entre-temps : on écarte cette couleur et on retentera avec une autre. */
          const variant = variantById(step.id);
          if (variant) variant.available = false;
          changed = true;
        } else {
          throw error;
        }
      }
    }

    if (left > 0 && plan.length === 0) setStatus('soldOut');
    return changed;
  }

  let chain = Promise.resolve();
  let syncQueued = false;

  function enqueue(task) {
    const run = chain.then(async () => {
      document.documentElement.setAttribute('data-es-gift-busy', '');
      try {
        await task();
      } catch (error) {
        console.error('[ES cadeau]', error);
        setStatus('genericError');
      } finally {
        document.documentElement.removeAttribute('data-es-gift-busy');
      }
    });

    chain = run;
    return run;
  }

  async function runSync(forceRefresh) {
    let changed = Boolean(forceRefresh);

    for (let pass = 0; pass < 5; pass++) {
      const passChanged = await syncOnce();
      changed = changed || passChanged;
      if (!passChanged) break;
    }

    if (changed) afterChange();
  }

  function sync() {
    if (syncQueued) return chain;
    syncQueued = true;

    return enqueue(async () => {
      syncQueued = false;
      await runSync(false);
    });
  }

  function afterChange() {
    if (config.pageType === 'cart') {
      /* Le panier pleine page est rendu côté serveur : on recharge, avec un garde-fou anti-boucle. */
      const now = Date.now();
      const reloads = (session.get(RELOAD_KEY) || '').split(',').map(Number).filter((time) => now - time < 15000);

      if (reloads.length < 3) {
        reloads.push(now);
        session.set(RELOAD_KEY, reloads.join(','));
        if (currentStatus) session.set(STATUS_KEY, currentStatus);
        window.location.reload();
        return;
      }
    }

    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('es-gift:updated'));
  }

  /* ------------------------------------------------------------------------------------------ */
  /* Actions du panier (couleur, refus, réactivation)                                            */
  /* ------------------------------------------------------------------------------------------ */

  function changeColor(lineKey, quantity, variantId) {
    setPreferred(variantId);

    return enqueue(async () => {
      const variant = variantById(variantId);

      if (!variant || !variant.available) {
        setStatus('colorError');
        await runSync(true);
        return;
      }

      try {
        await api('cart/change.js', { id: lineKey, quantity: 0 });
        await api('cart/add.js', { items: [{ id: variant.id, quantity, properties: { _es_gift: '1' } }] });
        if (currentStatus === 'colorError') setStatus(null);
      } catch (error) {
        if (error.status === 422) {
          variant.available = false;
          setStatus('colorError');
        } else {
          throw error;
        }
      }

      await runSync(true);
    });
  }

  function setOptOut(optedOut) {
    return enqueue(async () => {
      await api('cart/update.js', { attributes: { _es_gift_optout: optedOut ? '1' : '' } });
      await runSync(true);
    });
  }

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-es-gift-color]');
    if (!select) return;

    changeColor(select.getAttribute('data-line-key'), Number(select.getAttribute('data-quantity')) || 1, Number(select.value));
  });

  document.addEventListener('click', (event) => {
    const optOut = event.target.closest('[data-es-gift-optout]');
    const optIn = event.target.closest('[data-es-gift-optin]');

    if (optOut) {
      event.preventDefault();
      setOptOut(true);
    } else if (optIn) {
      event.preventDefault();
      setOptOut(false);
    }
  });

  /* Vérifie le panier juste avant le paiement. */
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const submitter = event.submitter;
    const isCheckout = (submitter && submitter.name === 'checkout') || (form.action && /\/cart\/?$/.test(new URL(form.action, location.href).pathname) && form.querySelector('[name="checkout"]'));

    if (!isCheckout || form.hasAttribute('data-es-gift-checked')) return;

    event.preventDefault();
    sync().then(() => {
      form.setAttribute('data-es-gift-checked', '');
      if (form.requestSubmit) {
        form.requestSubmit(submitter && submitter.form === form ? submitter : undefined);
      } else {
        form.submit();
      }
      setTimeout(() => form.removeAttribute('data-es-gift-checked'), 3000);
    });
  }, true);

  /* ------------------------------------------------------------------------------------------ */
  /* Déclencheurs                                                                                */
  /* ------------------------------------------------------------------------------------------ */

  document.addEventListener('cart:change', () => sync());
  document.addEventListener('cart-drawer:refreshed', renderStatus);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) sync();
  });

  window.EsGift = { config, getPreferred, setPreferred, sync, variantById };
  document.dispatchEvent(new CustomEvent('es-gift:ready'));

  renderStatus();
  sync();
})();
